import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import SocialEntryConfiguration from 'entity/SocialEntryConfiguration';
import SocialRoomSet from 'entity/SocialRoomSet';

import { ScootaverseEntryRulesService } from './../services/scootaverse-entry-rules.service';
import { ScootaversePageService } from './../services/scootaverse-page.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';

@Component({
  selector: 'entry-flows',
  templateUrl: './entry-flows.component.html',
  styleUrls: ['./entry-flows.component.scss'],
  standalone: false,
})
export class EntryFlowsComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);

  private entryFlowV1 = false;
  public loading = false;

  public scootaverseId?: string;
  public scootaverse?: SocialRoomSet;

  public flows?: SocialEntryConfiguration[];

  public constructor(
    private store: Store,
    private route: ActivatedRoute,
    private scootaversePageService: ScootaversePageService,
    private scootaverseEntryRulesService: ScootaverseEntryRulesService,
    private toasterService: ToastrService,
    public videoChatService: VideoChatService,
  ) {
    super();
    this.setDisplayName('EntryFlowsComponent');

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.entry_flows_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((entryFlowV1) => (this.entryFlowV1 = entryFlowV1));
  }

  public ngOnInit(): void {
    this.scootaverseId = this.route.snapshot.paramMap.get('id') || undefined;
    if (!this.scootaverseId || !this.entryFlowV1) {
      void this.scootaversePageService.redirectToHome();
    } else {
      this.loading = true;
      this.loadScootaverse(this.scootaverseId);
      this.loadFlows(this.scootaverseId);
    }
  }

  private loadScootaverse(scootaverseId: string): void {
    this.videoChatService
      .getSocialRoomSetPromise(scootaverseId)
      .then((scootaverseInfo) => {
        this.scootaverse = scootaverseInfo;
      })
      .catch((err) => {
        this.error('Error loading scootaverse information: ', err);
        void this.scootaversePageService.redirectToHome();
      });
  }

  private loadFlows(scootaverseId: string): void {
    this.scootaverseEntryRulesService
      .getEntryConfigurations(scootaverseId)
      .then((flows) => {
        this.flows = flows;
        this.loading = false;
      })
      .catch((err) => {
        this.info('Error loading entry configuration list: ', err);
        this.toasterService.error('Error loading entry flows. Try again later.');
        this.loading = false;
      });
  }

  protected createEntryFlow() {
    void this.scootaversePageService.onCreateEntryFlow(this.scootaverseId);
  }

  protected deleteEntryFlow(event: string) {
    this.loading = true;
    if (this.scootaverseId) {
      const socialRoomSetId = this.scootaverseId;
      this.scootaverseEntryRulesService
        .deleteEntryConfiguration(socialRoomSetId, event)
        .then(() => {
          this.toasterService.success('Entry flow deleted successfully');
          this.loadFlows(socialRoomSetId);
        })
        .catch((err) => {
          this.info('Error deleting entry flow: ', err);
          this.toasterService.error('Error deleting entry flow. Try again later.');
          this.loadFlows(socialRoomSetId);
        });
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
